import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Download,
  Info,
  Users,
  Calendar,
  Network,
  Building2,
  Briefcase,
  FileSpreadsheet,
  ClipboardList,
  GraduationCap,
  History,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { DesktopShell } from '@/components/desktop/DesktopShell';
import { ThreePanelLayout } from '@/components/desktop/layouts/ThreePanelLayout';
import { useActiveEvent } from '@/hooks/useActiveEvent';
import { downloadAdminCsv } from '@/lib/csvDownload';

const HISTORY_STORAGE_KEY = 'admin-export-history';
const HISTORY_LIMIT = 20;

type ExportDescriptor = {
  key: string;
  label: string;
  description: string;
  endpoint: string;
  fallbackName: string;
  icon: React.ReactNode;
};

type HistoryEntry = {
  id: string;
  key: string;
  label: string;
  filename: string;
  downloadedAt: string;
};

function withEvent(path: string, eventId?: string): string {
  return eventId ? `${path}?eventId=${encodeURIComponent(eventId)}` : path;
}

function loadHistory(): HistoryEntry[] {
  try {
    const raw = localStorage.getItem(HISTORY_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as HistoryEntry[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveHistory(entries: HistoryEntry[]): void {
  try {
    localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Quota or disabled storage — fall through silently; history is best-effort.
  }
}

function formatTimestamp(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function relativeAge(iso: string): string {
  const now = Date.now();
  const then = new Date(iso).getTime();
  const seconds = Math.max(1, Math.round((now - then) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

export default function DataPage() {
  const { event: activeEvent } = useActiveEvent();
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);

  const eventId = activeEvent?.id;
  const eventName = activeEvent?.name ?? 'Active event';

  useEffect(() => {
    setHistory(loadHistory());
  }, []);

  const recordDownload = (item: ExportDescriptor) => {
    const entry: HistoryEntry = {
      id: crypto.randomUUID(),
      key: item.key,
      label: item.label,
      filename: item.fallbackName,
      downloadedAt: new Date().toISOString(),
    };
    setHistory((prev) => {
      const next = [entry, ...prev].slice(0, HISTORY_LIMIT);
      saveHistory(next);
      return next;
    });
  };

  const handleDownload = async (item: ExportDescriptor) => {
    setBusyKey(item.key);
    try {
      await downloadAdminCsv(item.endpoint, item.fallbackName, item.label);
      recordDownload(item);
      toast.success(`${item.label} downloaded`);
    } finally {
      setBusyKey(null);
    }
  };

  const clearHistory = () => {
    setHistory([]);
    saveHistory([]);
  };

  const raisersEdgeExport: ExportDescriptor = {
    key: 'raisers-edge',
    label: 'Raiser’s Edge Registrant Export',
    description:
      'CSV formatted for direct upload into NPSF’s Raiser’s Edge CRM. Columns: Email, First name, Last name, Participant type, Attended, Service branch, Title / military rank, Organization, Job title, LinkedIn, Mailing list opt-in.',
    endpoint: withEvent('/admin/exports/raisers-edge.csv', eventId),
    fallbackName: 'raisers_edge_registrants.csv',
    icon: <FileSpreadsheet className="w-4 h-4" />,
  };

  const registrationExports: ExportDescriptor[] = [
    {
      key: 'exhibitor-applications',
      label: 'Industry Partner Applications',
      description:
        'Full registration data for every industry partner (exhibitor) application — all statuses, one row per submitted project.',
      endpoint: withEvent('/admin/exports/exhibitor-applications.csv', eventId),
      fallbackName: 'exhibitor_applications.csv',
      icon: <ClipboardList className="w-4 h-4" />,
    },
    {
      key: 'nps-project-submissions',
      label: 'Student & Faculty Project Submissions',
      description:
        'Full registration data for every NPS student/faculty project submission — all statuses, one row per submitted project.',
      endpoint: withEvent('/admin/exports/nps-project-submissions.csv', eventId),
      fallbackName: 'nps_project_submissions.csv',
      icon: <GraduationCap className="w-4 h-4" />,
    },
  ];

  const analyticsExports: ExportDescriptor[] = [
    {
      key: 'demographics',
      label: 'Demographics',
      description: 'Every approved participant with type, organization, role, check-in state.',
      endpoint: withEvent('/admin/exports/demographics.csv', eventId),
      fallbackName: 'demographics.csv',
      icon: <Users className="w-4 h-4" />,
    },
    {
      key: 'sessions',
      label: 'Session Fill Rates',
      description: 'Capacity, confirmed, waitlisted, and fill rate per session.',
      endpoint: withEvent('/admin/exports/sessions.csv', eventId),
      fallbackName: 'sessions.csv',
      icon: <Calendar className="w-4 h-4" />,
    },
    {
      key: 'workshop-registrants',
      label: 'Workshop Registrants',
      description:
        'Every confirmed workshop registrant with title, name, email, and workshop. Sortable by workshop.',
      endpoint: withEvent('/admin/exports/workshop-registrants.csv', eventId),
      fallbackName: 'workshop-registrants.csv',
      icon: <GraduationCap className="w-4 h-4" />,
    },
    {
      key: 'network',
      label: 'Network Engagement',
      description: 'Every favorited connection with both ends of the relationship.',
      endpoint: withEvent('/admin/exports/network.csv', eventId),
      fallbackName: 'network_engagement.csv',
      icon: <Network className="w-4 h-4" />,
    },
    {
      key: 'industry-favorites',
      label: 'Industry Favorites',
      description: 'Every (partner, participant) favorite — sortable and filterable by partner.',
      endpoint: withEvent('/admin/exports/industry-favorites.csv', eventId),
      fallbackName: 'industry_favorites.csv',
      icon: <Building2 className="w-4 h-4" />,
    },
    {
      key: 'project-favorites',
      label: 'Project Favorites',
      description: 'Every (project, participant) favorite — sortable and filterable by project.',
      endpoint: withEvent('/admin/exports/project-favorites.csv', eventId),
      fallbackName: 'project_favorites.csv',
      icon: <Briefcase className="w-4 h-4" />,
    },
  ];

  const dataSourceNotes = [
    {
      icon: <FileSpreadsheet className="w-4 h-4 text-primary" />,
      label: 'Raiser’s Edge Registrant Export',
      detail:
        'Approved participants formatted for NPSF’s Raiser’s Edge CRM upload. Mailing-list opt-in is True per the registration acknowledgement.',
    },
    {
      icon: <ClipboardList className="w-4 h-4 text-primary" />,
      label: 'Industry Partner Applications',
      detail:
        'Full registration data for every industry partner (exhibitor) application across all statuses. One row per submitted project, with the application columns repeated.',
    },
    {
      icon: <GraduationCap className="w-4 h-4 text-primary" />,
      label: 'Student & Faculty Project Submissions',
      detail:
        'Full registration data for every NPS student/faculty project submission across all statuses. One row per submitted project, with the submitter columns repeated.',
    },
    {
      icon: <Users className="w-4 h-4 text-primary" />,
      label: 'Demographics',
      detail:
        'Every approved participant — type, organization, role, branch of service, retired flag, check-in state, and walk-in flag.',
    },
    {
      icon: <Calendar className="w-4 h-4 text-primary" />,
      label: 'Session Fill Rates',
      detail:
        'One row per scheduled session with capacity, confirmed and waitlisted counts, and fill-rate percentage.',
    },
    {
      icon: <GraduationCap className="w-4 h-4 text-primary" />,
      label: 'Workshop Registrants',
      detail:
        'One row per confirmed workshop registrant — workshop title, Pacific start time, name, title, organization, and email. Sorted by workshop, then last/first name.',
    },
    {
      icon: <Network className="w-4 h-4 text-primary" />,
      label: 'Network Engagement',
      detail:
        'Every favorited connection with both ends of the relationship, organizations, participant types, and timestamp.',
    },
    {
      icon: <Building2 className="w-4 h-4 text-primary" />,
      label: 'Industry Favorites',
      detail:
        'Every (partner, participant) favorite across all industry partners. Sortable and filterable by partner.',
    },
    {
      icon: <Briefcase className="w-4 h-4 text-primary" />,
      label: 'Project Favorites',
      detail:
        'Every (project, participant) favorite across all research projects. Sortable and filterable by project.',
    },
  ];

  const InfoPanel = (
    <div className="h-full flex flex-col">
      <div className="h-[72px] px-4 flex items-center border-b border-border bg-muted flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Info className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="font-semibold text-lg">Data Sources</h2>
            <p className="text-sm text-muted-foreground">What each export contains</p>
          </div>
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          <div className="p-3 rounded-lg bg-muted/50 border border-border">
            <p className="text-xs text-muted-foreground font-medium mb-1">Scope</p>
            <p className="text-sm">{eventName}</p>
            <p className="text-xs text-muted-foreground mt-2">
              All exports are scoped to the currently active event. Switch the active event from the Events admin page
              to change scope.
            </p>
          </div>

          <div className="space-y-3">
            {dataSourceNotes.map((item) => (
              <div key={item.label} className="p-3 rounded-lg bg-muted/50 border border-border">
                <div className="flex items-center gap-2 mb-1">
                  {item.icon}
                  <span className="text-sm font-medium">{item.label}</span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">{item.detail}</p>
              </div>
            ))}
          </div>

          <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
            <p className="text-xs text-blue-800 dark:text-blue-200 font-medium mb-1">Note</p>
            <p className="text-xs text-blue-700 dark:text-blue-300 leading-relaxed">
              Files are RFC 4180-compliant CSVs with a header row. Open directly in Excel, Numbers, or Google Sheets —
              no transformation required for Raiser’s Edge upload.
            </p>
          </div>
        </div>
      </ScrollArea>
    </div>
  );

  const renderExportCard = (item: ExportDescriptor) => {
    const busy = busyKey === item.key;
    return (
      <Card key={item.key} className="border-primary/50 bg-primary/5">
        <CardHeader className="p-4 pb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-primary/15 text-primary">
              {item.icon}
            </div>
            <div className="min-w-0">
              <CardTitle className="text-sm">{item.label}</CardTitle>
              <CardDescription className="text-xs">{item.description}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <Button
            onClick={() => handleDownload(item)}
            disabled={busy}
            className="w-full h-10 text-sm justify-between"
            variant="default"
          >
            <span className="flex items-center gap-2">
              {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Download CSV
            </span>
          </Button>
        </CardContent>
      </Card>
    );
  };

  const ExportsPanel = (
    <div className="h-full flex flex-col">
      <div className="h-[72px] px-4 flex items-center border-b border-border bg-muted flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Download className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="font-semibold text-lg">CSV Exports</h2>
            <p className="text-sm text-muted-foreground">Raiser’s Edge + analytics downloads</p>
          </div>
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-6 space-y-4">
          {renderExportCard(raisersEdgeExport)}
          <div className="pt-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">
              Registration Data
            </p>
            <div className="space-y-3">
              {registrationExports.map((item) => renderExportCard(item))}
            </div>
          </div>
          <div className="pt-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">
              Analytics Exports
            </p>
            <div className="space-y-3">
              {analyticsExports.map((item) => renderExportCard(item))}
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );

  const HistoryPanel = (
    <div className="h-full flex flex-col">
      <div className="h-[72px] px-4 flex items-center border-b border-border bg-muted flex-shrink-0 justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <History className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0">
            <h2 className="font-semibold text-lg">Export History</h2>
            <p className="text-sm text-muted-foreground">Last {HISTORY_LIMIT} downloads</p>
          </div>
        </div>
        {history.length > 0 && (
          <Button variant="ghost" size="sm" className="text-xs flex-shrink-0" onClick={clearHistory}>
            Clear
          </Button>
        )}
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4">
          {history.length === 0 ? (
            <div className="text-center py-12">
              <History className="h-12 w-12 mx-auto mb-4 text-muted-foreground/30" />
              <p className="text-sm text-muted-foreground">No exports yet.</p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                Each CSV download will appear here with a timestamp.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {history.map((entry) => (
                <div
                  key={entry.id}
                  className="flex items-start justify-between gap-2 p-3 bg-muted rounded-lg hover:bg-muted/80 transition-colors"
                >
                  <div className="flex items-start gap-3 min-w-0">
                    <Download className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <p className="font-medium text-sm text-foreground truncate">{entry.label}</p>
                      <p className="text-xs text-muted-foreground truncate">{entry.filename}</p>
                      <p className="text-[11px] text-muted-foreground/80 mt-1">
                        {formatTimestamp(entry.downloadedAt)}
                      </p>
                    </div>
                  </div>
                  <Badge variant="secondary" className="text-[10px] flex-shrink-0">
                    {relativeAge(entry.downloadedAt)}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );

  return (
    <DesktopShell>
      <div className="h-full flex flex-col">
        <div className="px-6 py-4 border-b border-border bg-background flex-shrink-0">
          <h1 className="text-2xl font-bold">Data and Reporting</h1>
          <p className="text-sm text-muted-foreground mt-1">
            CSV exports for Raiser’s Edge upload and event analytics
          </p>
        </div>

        <div className="flex-1 overflow-hidden">
          <ThreePanelLayout
            left={InfoPanel}
            center={ExportsPanel}
            right={HistoryPanel}
            leftWidth="260px"
            equalCenterRight
          />
        </div>
      </div>
    </DesktopShell>
  );
}
